<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchPolicy,
  fetchPolicyBeneficiary,
  createPolicyBeneficiary,
  updatePolicyBeneficiary,
} from '@apis/admin/affiliate'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'
import PolicyBeneficiaryCondition from '@components/admin/affiliate-policy-beneficiary-condition'
import PolicyBenefactor from '@components/admin/affiliate-policy-benefactor'

export default {
  components: {
    PolicyBeneficiaryCondition,
    PolicyBenefactor,
  },
  props: {
    policy_seq: {
      type: Number,
      default: null,
    },
    beneficiary_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      policy_info: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    policy_seq() {
      this.init()
    },
    beneficiary_seq() {
      this.init()
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      this.loadPolicy()
        .then(() => Promise.all([this.loadBeneficiary()]))
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadBeneficiary() {
      const { beneficiary_info } = await fetchPolicyBeneficiary(
        this.policy_seq,
        this.beneficiary_seq
      )
      this.params = defaults(beneficiary_info, {
        beneficiary_type: 'MEMBER',
        invite_depth: 0,
        list_order: 0,
        is_global: 'N',
        policy_seq: this.policy_seq,
        seq: null,
        regdate: null,
      })
      this.info = beneficiary_info
    },
    async loadPolicy() {
      const { policy_info } = await fetchPolicy(this.policy_seq)

      this.policy_info = policy_info
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq
        ? updatePolicyBeneficiary
        : createPolicyBeneficiary
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t(
            'affiliate_policy_beneficiary_info.msg_allowance_policy_modify'
          )
        : this.$t(
            'affiliate_policy_beneficiary_info.msg_allowance_policy_register'
          )

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{
                  $t('affiliate_policy_beneficiary_info.beneficiary_type')
                }}</div></th
              >
              <td>
                <select v-model="params.beneficiary_type">
                  <option
                    v-for="val in constants.affiliate.policy_beneficiary
                      .beneficiary_type[policy_info.target_action]"
                    :key="val[0]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </td>
              <th scope="row">
                <div>
                  <template v-if="params.beneficiary_type === 'MEMBER'">{{
                    $t('affiliate_policy_beneficiary_info.member_unique_number')
                  }}</template>
                  <template v-else-if="params.beneficiary_type === 'INVITER'">{{
                    $t('affiliate_policy_beneficiary_info.referral_depth')
                  }}</template>
                </div>
              </th>
              <td>
                <input
                  v-if="params.beneficiary_type === 'MEMBER'"
                  v-model="params.member_seq"
                  type="text"
                  :class="[$style.inputTypeText, $style.w100, $style.tRight]"
                />
                <input
                  v-else-if="params.beneficiary_type === 'INVITER'"
                  v-model="params.invite_depth"
                  type="text"
                  :class="[$style.inputTypeText, $style.w100, $style.tRight]"
                />
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{
                  $t('affiliate_policy_beneficiary_info.output_order')
                }}</div></th
              >
              <td colspan="3"
                ><input
                  v-model="params.list_order"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              /></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('affiliate_policy_beneficiary_info.condition')"
      name="CONDITION"
    >
      <PolicyBeneficiaryCondition
        v-if="info && info.seq"
        :beneficiary_seq="info.seq"
      ></PolicyBeneficiaryCondition>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('affiliate_policy_beneficiary_info.benefit')"
      name="ACTION_CONDITION"
    >
      <PolicyBenefactor
        v-if="info && info.seq"
        :beneficiary_seq="info.seq"
      ></PolicyBenefactor>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
